import { GREY_800, GREY_900, SPACING_0_5, remCalc } from '@mnd-frontend/ui';
import type { TFunction } from 'i18next';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import type { NewsroomSetupSteps } from '../../../../modules/newsroomSetup/types';
import PreviewBranding from './PreviewBranding';
import PreviewCompanyDescription from './PreviewCompanyDescription';
import PreviewCompanyInformation from './PreviewCompanyInformation';
import PreviewPressContact from './PreviewPressContact';
import { boxShadow } from './shared';

const STEP_HEIGHT = 380;

const PageWrapper = styled.div`
  height: 100%;
`;

const PreviewHeader = styled.header`
  margin-bottom: ${remCalc(120)};
  h2 {
    font-size: ${remCalc(16)};
    margin: 0;
    color: ${GREY_900};
  }
  p {
    margin: 0;
    margin-top: ${SPACING_0_5};
    font-size: ${remCalc(14)};
    color: ${GREY_800};
  }
`;

const StepsWrapper = styled.div`
  position: relative;
  overflow: hidden;
  height: ${STEP_HEIGHT}px;
  display: flex;
  align-items: center;
  ${boxShadow}
`;

const Steps = styled.div<{ $top: number }>`
  position: absolute;
  top: 0;
  width: 100%;
  transition: transform 0.4s ease-in-out;
  transform: translateY(${({ $top }) => -$top}px);
`;

const Step = styled.div`
  height: ${STEP_HEIGHT}px;
`;

const getNewsroomPreviewDescription = (step: NewsroomSetupSteps, t: TFunction) => {
  switch (step) {
    case 'branding':
      return t('newsroom-setup_branding-description');
    case 'press-contact':
      return t('newsroom-setup_press-contact-description');
    case 'company-description':
      return t('newsroom-setup_company-description-description');
    case 'company-info':
      return t('newsroom-setup_company-info-description');
    default:
      return '';
  }
};

const NewsroomPreview = ({ step }: { step: NewsroomSetupSteps }) => {
  const { t } = useTranslation();
  const [top, setTop] = useState(0);

  useEffect(() => {
    switch (step) {
      case 'branding':
        setTop(0);
        break;
      case 'press-contact':
        setTop(STEP_HEIGHT);
        break;
      case 'company-description':
        setTop(STEP_HEIGHT * 2);
        break;
      case 'company-info':
        setTop(STEP_HEIGHT * 3);
        break;
      default:
        setTop(0);
        break;
    }
  }, [step]);

  return (
    <PageWrapper>
      <PreviewHeader>
        <h2>{t('newsroom-setup_quick-preview')}</h2>
        <p>{getNewsroomPreviewDescription(step, t)}</p>
      </PreviewHeader>
      <StepsWrapper>
        <Steps $top={top}>
          <Step>
            <PreviewBranding />
          </Step>
          <Step>
            <PreviewPressContact />
          </Step>
          <Step>
            <PreviewCompanyDescription />
          </Step>
          <Step>
            <PreviewCompanyInformation />
          </Step>
        </Steps>
      </StepsWrapper>
    </PageWrapper>
  );
};

export default NewsroomPreview;
